import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface SidebarCardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'purple' | 'blue' | 'green' | 'amber' | 'indigo';
  isAccordion?: boolean;
  isExpanded?: boolean;
  noPadding?: boolean;
  isLoading?: boolean;
  loadingItems?: number;
}

const SidebarCard = ({ 
  children, 
  className, 
  variant = 'default',
  isAccordion = false,
  isExpanded = true,
  noPadding = false,
  isLoading = false,
  loadingItems = 3,
  ...props 
}: SidebarCardProps) => {
  // Base styles with enhanced modern appearance
const baseStyles = [
  // Clean background colors
  'bg-white/70',
  'dark:bg-black/35',
  // Smooth transitions
  'transition-all duration-200'
].join(' ');

// Variant styles - currently not used for borders
const variantStyles = {
  default: "",
  purple: "",
  blue: "",
  green: "",
  amber: "",
  indigo: ""
};

  return (
    <div
      className={cn(
        // Base structure
        "relative rounded-2xl overflow-visible",
        // Base styles
        baseStyles,
        // Variant styles (currently unused but kept for future use)
        variantStyles[variant],
        // Consistent blur
        "backdrop-blur-xl",
        // Full height for proper layout
        "h-full",
        // Consistent padding
        !noPadding && (isAccordion ? "" : "p-6"),
        className
      )}
      {...props}
    >
      {/* If loading, show skeleton items for dynamic content only */}
      {isLoading ? (
        <>
          {/* Find and render the SidebarHeader if it exists */}
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child) && 
                child.type && 
                typeof child.type !== 'string' && 
                'displayName' in child.type && 
                child.type.displayName === 'SidebarHeader') {
              return child;
            }
            return null;
          })}
          
          {/* Render skeleton items for the content */}
          <div className="space-y-4 mt-6">
            {Array.from({ length: loadingItems }).map((_, index) => (
              <div key={`skeleton-item-${index}`} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-4 rounded-full bg-slate-200/80 dark:bg-slate-700/50" />
                  <Skeleton className="h-4 w-24 bg-slate-200/80 dark:bg-slate-700/50" />
                </div>
                <Skeleton className="h-5 w-5 rounded-full bg-slate-200/80 dark:bg-slate-700/50" />
              </div>
            ))}
          </div>
        </>
      ) : (
        /* Process children and handle accordion content */
        React.Children.map(children, (child, index) => {
          if (React.isValidElement(child) && 
              child.type && 
              typeof child.type !== 'string' && 
              'displayName' in child.type && 
              child.type.displayName === 'SidebarHeader') {
            // Return SidebarHeader as-is for accordion handling
            return child;
          }
          
          // Handle all non-SidebarHeader content
          // On desktop (not accordion), render content normally with consistent spacing
          if (!isAccordion) {
            return (
              <div key={`content-${index}`} className="space-y-2 mt-6">
                {child}
              </div>
            );
          }
          
          // On mobile (accordion mode), use animation for all non-SidebarHeader content
          return (
            <AnimatePresence initial={false} key={`accordion-${index}`}>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{
                    height: { duration: 0.3, ease: 'easeInOut' },
                    opacity: { duration: 0.2, delay: isExpanded ? 0.1 : 0 }
                  }}
                  className="p-3 md:px-6 md:pb-6 space-y-2 overflow-visible"
                >
                  {child}
                </motion.div>
              )}
            </AnimatePresence>
          );
        })
      )}
    </div>
  );
};

export default SidebarCard;
